/**
 * Tenant Table Component
 *
 * Table display for tenant list with actions.
 *
 * @fileoverview Tenant data table component
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  Text,
  Skeleton,
  useColorModeValue,
  Icon,
  Flex,
  Button,
  HStack,
  VStack,
  Tooltip,
  Wrap,
  WrapItem,
  Select,
} from '@chakra-ui/react';
import {
  MdMoreVert,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdCheckCircle,
  MdCancel,
  MdChevronLeft,
  MdChevronRight,
  MdAccountBalance,
  MdSwitchAccount,
  MdArrowUpward,
  MdArrowDownward,
  MdUnfoldMore,
} from 'react-icons/md';
import type { AdminTenant, PaginationState, TenantQueryParams } from '../../types/admin';

interface TenantTableProps {
  tenants: AdminTenant[];
  loading: boolean;
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onView: (tenant: AdminTenant) => void;
  onEdit: (tenant: AdminTenant) => void;
  onDelete: (tenant: AdminTenant) => void;
  onActivate: (tenant: AdminTenant) => void;
  onDeactivate: (tenant: AdminTenant) => void;
  onSupportMode?: (tenant: AdminTenant) => void;
  sortBy?: TenantQueryParams['sortBy'];
  sortOrder?: TenantQueryParams['sortOrder'];
  onSort?: (sortBy: TenantQueryParams['sortBy'], sortOrder: TenantQueryParams['sortOrder']) => void;
}

/**
 * Tenant Table Component
 *
 * Displays tenants in a table with actions.
 */
export const TenantTable: React.FC<TenantTableProps> = ({
  tenants,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onSupportMode,
  sortBy,
  sortOrder,
  onSort,
}) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const sortIconColor = useColorModeValue('gray.300', 'gray.600');
  const sortActiveColor = useColorModeValue('cyan.600', 'cyan.300');

  function SortableTh({ field, label, isNumeric }: { field: TenantQueryParams['sortBy']; label: string; isNumeric?: boolean }) {
    const isActive = sortBy === field;
    const nextOrder = isActive && sortOrder === 'asc' ? 'desc' : 'asc';
    return (
      <Th
        isNumeric={isNumeric}
        cursor={onSort ? 'pointer' : 'default'}
        userSelect="none"
        onClick={() => onSort?.(field, nextOrder)}
        _hover={onSort ? { color: sortActiveColor } : undefined}
        bg={bgColor}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'}>
          <Text as="span">{label}</Text>
          <Icon
            as={isActive ? (sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward) : MdUnfoldMore}
            boxSize={3}
            color={sortActiveColor}
          />
        </HStack>
      </Th>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get color for Plaid status badge
  const getPlaidStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'LOGIN_REQUIRED':
        return 'orange';
      case 'REVOKED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleRowClick = (tenant: AdminTenant) => {
    navigate(`/admin/tenants/${tenant.id}`);
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Table>
          <Thead>
            <Tr>
              <SortableTh field="name" label="Name" />
              <Th>Slug</Th>
              <Th>Manager</Th>
              <Th>Status</Th>
              <Th>Bank</Th>
              <Th>Members</Th>
              <SortableTh field="createdAt" label="Created" />
              <Th isNumeric>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <Tr key={i}>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="60px" /></Td>
                <Td><Skeleton h="20px" w="80px" /></Td>
                <Td><Skeleton h="20px" w="40px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="40px" /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  // Empty state
  if (tenants.length === 0) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={8}
        textAlign="center"
      >
        <Text color="gray.500">No tenants found</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/*
        maxH + overflowY=auto makes this Box the scroll container the
        <Thead> below anchors its `position: sticky` to — without a bounded
        height, `position: sticky` is inert (it anchors to the nearest
        *scrolling* ancestor, and a Box that only ever grows to fit its
        content never scrolls internally). See TransactionTable's
        `containedScroll` prop doc and
        docs/plans/UI_LAYOUT_DENSITY_PLAN.md §H4/H8 for the same fix
        applied to the transactions tables.
      */}
      <Box
        bg={bgColor}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        overflow="auto"
        maxH="calc(100vh - 260px)"
      >
        <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead position="sticky" top={0} zIndex={1}>
            <Tr>
              <SortableTh field="name" label="Name" />
              <Th bg={bgColor} borderBottomWidth="1px" borderColor={borderColor}>Slug</Th>
              <Th bg={bgColor} borderBottomWidth="1px" borderColor={borderColor}>Manager</Th>
              <Th bg={bgColor} borderBottomWidth="1px" borderColor={borderColor}>Status</Th>
              <Th bg={bgColor} borderBottomWidth="1px" borderColor={borderColor}>Bank</Th>
              <Th bg={bgColor} borderBottomWidth="1px" borderColor={borderColor}>Members</Th>
              <SortableTh field="createdAt" label="Created" />
              <Th bg={bgColor} borderBottomWidth="1px" borderColor={borderColor} isNumeric>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tenants.map((tenant) => (
              <Tr
                key={tenant.id}
                _hover={{ bg: hoverBg }}
                cursor="pointer"
                onClick={() => handleRowClick(tenant)}
              >
                <Td fontWeight="medium">{tenant.name}</Td>
                <Td>
                  <Text fontSize="sm" color="gray.500" fontFamily="mono">
                    {tenant.slug}
                  </Text>
                </Td>
                <Td>
                  {tenant.managerEmail ? (
                    <Box>
                      {tenant.managerName && (
                        <Text fontSize="sm">{tenant.managerName}</Text>
                      )}
                      <Text fontSize="xs" color="gray.500">
                        {tenant.managerEmail}
                      </Text>
                    </Box>
                  ) : (
                    <Text fontSize="sm" color="gray.400">—</Text>
                  )}
                </Td>
                <Td>
                  <Badge
                    colorScheme={tenant.isActive ? 'green' : 'red'}
                    variant="subtle"
                  >
                    {tenant.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>
                  {(tenant.plaidTokens && tenant.plaidTokens.length > 0) ? (
                    <Wrap spacing={1}>
                      {tenant.plaidTokens.map((token) => (
                        <WrapItem key={token.id}>
                          <Tooltip label={token.status.replace(/_/g, ' ')} placement="top" hasArrow>
                            <Badge
                              colorScheme={getPlaidStatusColor(token.status)}
                              variant="subtle"
                              fontSize="xs"
                            >
                              <Flex align="center" gap={1}>
                                <Icon as={MdAccountBalance} boxSize={3} />
                                {token.institutionName || 'Connected'}
                              </Flex>
                            </Badge>
                          </Tooltip>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Text fontSize="sm" color="gray.400">—</Text>
                  )}
                </Td>
                <Td>
                  <Text fontSize="sm">
                    {tenant._count?.members ?? 0}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" color="gray.500">
                    {formatDate(tenant.createdAt)}
                  </Text>
                </Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <HStack spacing={1} justify="flex-end">
                    {onSupportMode && (
                      <Tooltip label="View tenant's transactions" placement="top" hasArrow>
                        <IconButton
                          aria-label="Enter support mode"
                          icon={<Icon as={MdSwitchAccount} boxSize={4} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="purple"
                          _hover={{
                            bg: 'purple.50',
                            color: 'purple.600',
                            transform: 'scale(1.1)',
                            shadow: 'sm',
                          }}
                          transition="all 0.15s"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSupportMode(tenant);
                          }}
                        />
                      </Tooltip>
                    )}
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<Icon as={MdMoreVert} />}
                      variant="ghost"
                      size="sm"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<Icon as={MdEdit} />}
                        onClick={() => onEdit(tenant)}
                      >
                        Edit
                      </MenuItem>
                      {tenant.isActive ? (
                        <MenuItem
                          icon={<Icon as={MdCancel} />}
                          onClick={() => onDeactivate(tenant)}
                        >
                          Deactivate
                        </MenuItem>
                      ) : (
                        <MenuItem
                          icon={<Icon as={MdCheckCircle} />}
                          onClick={() => onActivate(tenant)}
                        >
                          Activate
                        </MenuItem>
                      )}
                      <MenuItem
                        icon={<Icon as={MdDelete} />}
                        color="red.500"
                        onClick={() => onDelete(tenant)}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Menu>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.500">Rows per page:</Text>
          <Select
            size="sm"
            w="80px"
            value={pagination.limit}
            onChange={e => onPageSizeChange?.(Number(e.target.value))}
          >
            {[5, 10, 20, 50].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500">
            {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </Text>
        </HStack>
        <Flex gap={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={MdChevronLeft} />}
            isDisabled={pagination.page <= 1}
            onClick={() => onPageChange(pagination.page - 1)}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            rightIcon={<Icon as={MdChevronRight} />}
            isDisabled={pagination.page >= pagination.totalPages}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default TenantTable;
